<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import ModalContainer from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import GridContainer from '@/components/grid-container.vue'
import GridItem from '@/components/grid-item.vue'
import InputText from '@/components/input-text.vue'
import InputLabel from '@/components/input-label.vue'
import InputSelect from '@/components/input-select.vue'
import InputDate from '@/components/input-date.vue'
import InputNumber from '@/components/input-number.vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { meterBillingRegisters, MeterStatus, MeterType, meterTypes, MeterBillingRegister } from '@/api/assets/constants'
import ModalFooter from '@/components/modal/modal-footer.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { faFloppyDisk, faTimes } from '@fortawesome/pro-regular-svg-icons'
import ButtonSecondary from '@/components/button-secondary.vue'
import InputTime from '@/components/input-time.vue'
import { useLinkedMeters, useLinkMeter, useUnlinkedMeters, useScalingFactors } from '@/composables/assets/meters'
import { DateFormats } from '@/helpers/formatting'
import { format } from 'date-fns/format'
import HeroSplashError from '@/components/hero-splash-error.vue'
import { ActualMeterOperationalFormSchema, ActualMeterDecommissionedFormSchema, VirtualMeterFormSchema, VirtualGridMeterFormSchema } from '@/api/assets/schemas'
import InputHidden from '@/components/input-hidden.vue'
import useNotifications from '@/composables/notifications'
import TooltipIcon from '@/shared/components/TooltipIcon.vue'

const emit = defineEmits<{ close: [] }>()
const props = defineProps<{ assetCode: string, meterId?: number }>()
const { addSuccess, addError } = useNotifications()

const selectedMeterType = ref<MeterType>()
const selectedMeterStatus = ref<MeterStatus|null>(null)

const { data: scalingFactors } = useScalingFactors()
const { data: linkedMeters } = useLinkedMeters(
  toRef(props, 'assetCode')
)

const isUpdating = computed(() => !!props.meterId)

const meterDetails = computed(
  () => isUpdating.value && linkedMeters.value !== undefined
    ? linkedMeters.value!.find(x => x.id === props.meterId)
    : undefined
)

const availableScalingFactors = computed(
  () => scalingFactors.value || []
)

const { values: currentValues, handleSubmit } = useForm({
  validationSchema: computed(
    () => {

      if (selectedMeterStatus.value === MeterStatus.Decommissioned || meterDetails.value?.meterStatus === MeterStatus.Decommissioned) {
        return toTypedSchema(ActualMeterDecommissionedFormSchema)
      }

      if (selectedMeterType.value === MeterType.VirtualSolar || meterDetails.value?.meterType === MeterType.VirtualSolar) {
        return toTypedSchema(VirtualMeterFormSchema)
      }

      if(selectedMeterType.value == MeterType.VirtualGrid || meterDetails.value?.meterType === MeterType.VirtualGrid){
        return toTypedSchema(VirtualGridMeterFormSchema)
      }

      return toTypedSchema(ActualMeterOperationalFormSchema)
    }
  )
})

const { data: unlinkedMeters, isFetching: isFetchingUnlinkedMeters } = useUnlinkedMeters(
  toRef(props, 'assetCode')
)

watch(
  meterDetails,
  (newValue) => {
    if (!newValue) return;

    if (selectedMeterType.value !== newValue.meterType) {
      selectedMeterType.value = newValue.meterType;
    }

    if (selectedMeterStatus.value !== newValue.meterStatus) {
      selectedMeterStatus.value = newValue.meterStatus;
    }
  },
  { immediate: true }
)

const selectedUnlinkedMeter = computed(
  () => unlinkedMeters.value?.find(x => x.deviceId === currentValues.deviceId)
)

const { mutateAsync: linkMeterAsync, isPending: isLinkingMeter } = useLinkMeter(toRef(props, 'assetCode'))

const doSubmit = handleSubmit(
  async (values) => {
    try {
      await linkMeterAsync(values)

      addSuccess({
        title: 'Meter linked successfully.',
        allowHtml: true,
        message: `Meter <b>${values.name}</b> was ${values.id === undefined ? 'created' : 'updated'} successfully.`
      })

      emit('close')
    } catch {
      addError({
        title: 'Meter not linked.',
        allowHtml: true,
        message: `This meter could not be ${values.id === undefined ? 'created' : 'updated'}. Please try again.`
      })
    }
  }
)

const availableVcomIds = computed(
  () => {
    // Only select all unlinked meters that have a VCOM device ID.
    let metersToSelectFrom = (unlinkedMeters.value ?? []).filter(x => typeof x.vcomDeviceId === 'string')

    // Filter out for matching UIDs if already selected.
    if (currentValues.deviceUid) {
      metersToSelectFrom = metersToSelectFrom.filter(x => x.deviceUid === currentValues.deviceUid)
    }

    const availableIds = metersToSelectFrom.map(x => x.vcomDeviceId)

    if (meterDetails.value?.vcomDeviceId) {
      availableIds.push(meterDetails.value.vcomDeviceId)
    }

    return Array.from(new Set(availableIds)).map(x => ({ text: x, value: x }))
  }
)

const availableDeviceUids = computed(
  () => {
    const alreadyLinkedUids = (linkedMeters.value ?? []).map(x => x.deviceUid).filter(x => typeof x === 'string')

    let metersToSelectFrom = (unlinkedMeters.value ?? [])
      .filter(x => typeof x.deviceUid === 'string')
      .filter(x => !alreadyLinkedUids.includes(x.deviceUid!))

    if (currentValues.vcomDeviceId) {
      metersToSelectFrom = metersToSelectFrom.filter(x => x.vcomDeviceId === currentValues.vcomDeviceId)
    }

    const availableIds = metersToSelectFrom.map(x => x.deviceUid)

    if (meterDetails.value?.deviceUid) {
      availableIds.push(meterDetails.value.deviceUid)
    }

    return Array.from(new Set(availableIds)).map(x => ({text: x, value: x}))
  }
)

const availableDeviceIds = computed(
  () => (unlinkedMeters.value ?? [])
    .map(x => x.deviceId)
    .concat(
      isUpdating.value && meterDetails.value?.meterStatus === MeterStatus.Decommissioned
        ? (linkedMeters.value ?? []).map(x => x.deviceId)
        : []
    )
    .concat(meterDetails.value ? [meterDetails.value!.deviceId] : [])
    .reduce(
      (accumulator, current) => accumulator.includes(current) ? accumulator : [...accumulator, current],
      [] as string[]
    )
    .sort()
    .map(x => ({ deviceId: x }))
)

const availableMeterTypes = computed(
  () => meterDetails.value?.meterType === MeterType.VirtualSolar || meterDetails.value?.meterType === MeterType.VirtualGrid
    ? meterTypes
    : meterTypes.filter(x => x.value !== MeterType.VirtualSolar)
)

const onMeterTypeChange = (newType: MeterType) => {
  if (selectedMeterType.value !== newType) {
    selectedMeterType.value = newType;
  }
}

const computedBillingRegister = computed(() => {
  if (!currentValues.billingRegister && !meterDetails.value?.billingRegister) {
    return selectedMeterType.value === MeterType.Grid
      ? MeterBillingRegister.ReverseActiveEnergy
      : undefined;
  }
  return meterDetails.value?.billingRegister || currentValues.billingRegister;
})

const commissioningDateValue = computed(
  () => {
    if (meterDetails.value) {
      return meterDetails.value.commissioning
    }

    if (selectedUnlinkedMeter.value) {
      return selectedUnlinkedMeter.value.commissioning
    }

    return undefined
  }
)
</script>

<template>
  <ModalContainer>
    <ModalTitle>Link Meters</ModalTitle>
    <ModalContent>
      <HeroSplashError v-if="meterId && !meterDetails" title="Meter not found." />

      <GridContainer v-else :columns="[164, '1fr']" :row-gap="10" :column-gap="10" align="center">
        <InputHidden name="id" :value="meterDetails?.id" />

        <GridItem><InputLabel>Status</InputLabel></GridItem>
        <GridItem>
          <InputSelect name="meterStatus"
                       :disabled="meterDetails?.meterStatus === MeterStatus.Decommissioned || meterDetails?.meterType === MeterType.VirtualSolar || meterDetails?.meterType === MeterType.VirtualGrid"
                       error-variant="tooltip"
                       :items="[
                         { text: 'Operational', value: MeterStatus.Operational },
                         { text: 'Decommissioned', value: MeterStatus.Decommissioned },
                       ]"
                       :selected="meterDetails?.meterStatus"
                       @update:selected="selectedMeterStatus = $event"
                       item-text="text"
                       item-value="value" />
        </GridItem>

        <GridItem>
          <InputLabel>
            Device UID
            <TooltipIcon text="Hardware serial number of a meter." class="ms-1" placement="bottom"/>
          </InputLabel>
        </GridItem>
        <GridItem>
          <InputSelect name="deviceUid"
                         item-text="text"
                         item-value="value"
                         :items="availableDeviceUids"
                         :selected="meterDetails?.deviceUid"
                         :disabled="meterDetails?.meterType === MeterType.VirtualSolar || meterDetails?.meterType === MeterType.VirtualGrid"
                         :placeholder="isFetchingUnlinkedMeters ? 'Loading...' : undefined"/>
        </GridItem>

        <GridItem>
          <InputLabel>
            Device ID
            <TooltipIcon text="This is used to match a meter to its reading and measurements." class="ms-1" placement="bottom"/>
          </InputLabel>
        </GridItem>
        <GridItem>
          <InputSelect name="deviceId"
                       error-variant="tooltip"
                       :placeholder="isFetchingUnlinkedMeters ? 'Loading...' : undefined"
                       :items="availableDeviceIds"
                       :disabled="meterDetails?.meterType === MeterType.VirtualSolar || meterDetails?.meterType === MeterType.VirtualGrid"
                       :selected="meterDetails?.deviceId"
                       item-text="deviceId"
                       item-value="deviceId" />
        </GridItem>

        <template v-if="meterDetails?.meterType !== MeterType.VirtualSolar">
          <GridItem>
            <InputLabel>VCOM Device ID
              <TooltipIcon text="Enter the numeric deviceID exactly as it appears in the VCOM software." class="ms-1" placement="bottom"/>
            </InputLabel>
          </GridItem>
          <GridItem>
            <InputSelect name="vcomDeviceId"
                         :items="availableVcomIds"
                         item-text="text"
                         item-value="value"
                         :placeholder="isFetchingUnlinkedMeters ? 'Loading...' : undefined"
                         error-variant="tooltip"
                         :selected="meterDetails?.vcomDeviceId ?? selectedUnlinkedMeter?.vcomDeviceId" />
          </GridItem>
        </template>

        <GridItem><InputLabel>Device Name</InputLabel></GridItem>
        <GridItem><InputText name="name" error-variant="tooltip" :value="meterDetails?.name" /></GridItem>

        <GridItem><InputLabel>Type</InputLabel></GridItem>
        <GridItem>
          <InputSelect name="meterType"
                       error-variant="tooltip"
                       :items="availableMeterTypes"
                       :disabled="meterDetails?.meterType === MeterType.VirtualSolar || meterDetails?.meterType === MeterType.VirtualGrid"
                       :selected="selectedMeterType"
                       @update:selected="onMeterTypeChange"
                       item-text="text"
                       item-value="value" />
        </GridItem>

        <template v-if="meterDetails?.meterType !== MeterType.VirtualSolar && meterDetails?.meterType !== MeterType.VirtualGrid">
          <GridItem><InputLabel>Serial No.</InputLabel></GridItem>
          <GridItem><InputText name="serialNumber" error-variant="tooltip" :value="meterDetails?.serialNumber" /></GridItem>

          <GridItem><InputLabel>Vendor</InputLabel></GridItem>
          <GridItem><InputText name="vendor" error-variant="tooltip" :value="meterDetails?.vendor" /></GridItem>

          <GridItem><InputLabel>Model</InputLabel></GridItem>
          <GridItem><InputText name="model" error-variant="tooltip" :value="meterDetails?.model" /></GridItem>
        </template>

        <GridItem><InputLabel>Description</InputLabel></GridItem>
        <GridItem><InputText name="description" error-variant="tooltip" :value="meterDetails?.description" /></GridItem>

        <GridItem><InputLabel>Billing Register</InputLabel></GridItem>
        <GridItem>
          <InputSelect name="billingRegister"
                       error-variant="tooltip"
                       :items="meterBillingRegisters"
                       :selected="computedBillingRegister"
                       item-text="text"
                       item-value="value" />
        </GridItem>
        <GridItem><InputLabel>Scaling Factor</InputLabel></GridItem>
        <GridItem>
          <InputSelect name="scalingFactorId"
                       error-variant="tooltip"
                       :items="availableScalingFactors"
                       :selected="meterDetails?.scalingFactorId"
                       item-text="factor"
                       item-value="id"
                       :placeholder="availableScalingFactors.length == 0 ? '....' : undefined"/>
        </GridItem>

        <template v-if="selectedMeterType !== MeterType.VirtualSolar && selectedMeterType !== MeterType.VirtualGrid">
          <GridItem><InputLabel>Commissioning Date & Time</InputLabel></GridItem>
          <GridItem>
            <GridContainer :columns="['2fr', '1fr']">
              <GridItem><InputDate name="commissionedAtDate" error-variant="tooltip" :max="selectedMeterStatus === MeterStatus.Decommissioned ? currentValues.decommissionedAtDate : undefined" :value="commissioningDateValue" /></GridItem>
              <GridItem><InputTime name="commissionedAtTime" :value="commissioningDateValue ? format(commissioningDateValue!, DateFormats.HourMinute) : undefined" /></GridItem>
            </GridContainer>
          </GridItem>

          <GridItem><InputLabel>Take-On Reading</InputLabel></GridItem>
          <GridItem><InputNumber name="takeOnReading" error-variant="tooltip" :value="meterDetails?.takeOnReading" /></GridItem>
        </template>

        <template v-if="currentValues.meterStatus === MeterStatus.Decommissioned">
          <GridItem><InputLabel>Decommissioning Date & Time</InputLabel></GridItem>
          <GridItem>
            <GridContainer :columns="['2fr', '1fr']">
              <GridItem><InputDate name="decommissionedAtDate" error-variant="tooltip" :min="currentValues.commissionedAtDate" :value="meterDetails?.deCommissioning" /></GridItem>
              <GridItem><InputTime name="decommissionedAtTime" :value="meterDetails?.deCommissioning ? format(meterDetails.deCommissioning, DateFormats.HourMinute) : undefined" /></GridItem>
            </GridContainer>
          </GridItem>

          <GridItem><InputLabel>Close-Out Reading</InputLabel></GridItem>
          <GridItem><InputNumber name="closeOutReading" error-variant="tooltip" :value="meterDetails?.closeOutReading" /></GridItem>
        </template>
      </GridContainer>
    </ModalContent>

    <ModalFooter class="text-end">
      <ButtonSecondary @click="$emit('close')" :icon="faTimes" :disabled="isLinkingMeter">Close</ButtonSecondary>
      <ButtonPrimary class="ms-3" @click="doSubmit" :icon="faFloppyDisk" :preset="isLinkingMeter ? 'loading' : undefined">Save</ButtonPrimary>
    </ModalFooter>
  </ModalContainer>
</template>

<style scoped lang="scss">

</style>
